/**
 * PATH: packages/email-hub/src/feature/components/CardAiTag.tsx
 * FILE_ID: EMAILHUB-FEATURE-COMPONENT-CARDAITAG-001
 * PURPOSE: The inbox card's round, 3-colour "AI" status dot — a single
 *          AI glyph that CHANGES COLOUR by lifecycle state:
 *            • SLATE/grey  (pending)  — AI suggested, awaiting YOUR ✓.
 *                          Stays private to you; does NOT push to the
 *                          shared Kanban board until you approve it
 *                          (your org is in review/manual-approval mode).
 *            • VIOLET      (auto)     — AI auto-filed it onto the board
 *                          because auto-accept is ON in Settings; not
 *                          yet human-verified.
 *            • EMERALD/green (accepted) — verified: accepted by you or
 *                          your team; on the shared board.
 * WHY:     Operator 2026-06-18: "an AI literally AI round icon at the
 *          edge of the box that changes colors" for the three states we
 *          discussed (manual-approval vs auto-pushed vs verified). The
 *          prior tag was a tiny text pill buried in the card's chip
 *          strip — hard to see, and the auto vs pending distinction (and
 *          where the auto-push setting lives) wasn't legible. This makes
 *          it a solid, glanceable colour dot pinned to the card's edge,
 *          with a tooltip that NAMES the controlling setting.
 * FLOW:    CardAiState -> <span> (one AI glyph, colour by state).
 *          Renders nothing for 'none' so uncategorised rows stay clean.
 * RELATED:
 *   - lib/cardAiState.ts: resolveCardAiState -> `state`.
 *   - components/CardVisibilityBadge.tsx: the textual Private/On-board
 *     twin that reinforces this colour (a11y: state is not colour-only).
 *   - settings-hub EmailAiTriageTabContent.tsx: the auto-push setting the
 *     tooltip points at (off / review / auto-accept).
 */
import type { ReactElement } from 'react'
import { Bot } from 'lucide-react'

import type { CardAiState } from '../lib/cardAiState'
import { cn } from '../../_internal/lib/utils'

type Visible = Exclude<CardAiState, 'none'>

/** One AI glyph, three colours. The colour is the signal (operator:
 *  "changes colors"); the tooltip narrates the state AND tells the
 *  operator exactly where the auto-push policy lives so the violet
 *  "auto" state is no longer a mystery. */
const STYLE: Record<Visible, { cls: string; title: string }> = {
  pending: {
    cls: 'bg-slate-200 text-slate-600 ring-slate-300/80 dark:bg-slate-700 dark:text-slate-200 dark:ring-slate-500/70',
    title:
      'AI suggested — awaiting YOUR approval. Private until you accept (✓). Org policy: Settings → AI Triage. Your auto-push: Settings → Email → Let AI file emails automatically (off).',
  },
  auto: {
    cls: 'bg-violet-500 text-white ring-violet-300 dark:bg-violet-500 dark:ring-violet-400/60',
    title:
      'AI auto-filed onto the board because YOUR mailbox auto-push is ON (Settings → Email → Let AI file emails automatically). Org AI Triage must also allow auto-accept. Open to verify.',
  },
  accepted: {
    cls: 'bg-emerald-500 text-white ring-emerald-300 dark:bg-emerald-500 dark:ring-emerald-400/60',
    title:
      'Verified — accepted by you or your team. On the shared board.',
  },
}

export function CardAiTag({
  state,
  className,
}: {
  state: CardAiState
  className?: string
}): ReactElement | null {
  if (state === 'none') return null
  const { cls, title } = STYLE[state]
  return (
    <span
      data-testid="card-ai-tag"
      data-ai-state={state}
      title={title}
      aria-label={title}
      role="img"
      className={cn(
        // Round, solid colour dot — the AI status reads as one glanceable
        // pip at the card's edge, not a word buried in the chip strip.
        'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ring-2 shadow-sm',
        cls,
        className,
      )}
    >
      <Bot className="h-3 w-3" aria-hidden />
    </span>
  )
}

export default CardAiTag
